import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import ReactSelect, { components } from 'react-select';
import { ThemeContext } from 'styled-components';
import FieldWrapper from '../../FieldWrapper';
import { useCustomStyles } from './hooks';
import IndicatorsContainer from './IndicatorsContainer';
import Option from './Option';
import Placeholder from './Placeholder';
import { BaseSelectItem, SelectProps } from './types';

function SelectBase<
  T = string,
  P extends BaseSelectItem<T> = BaseSelectItem<T>
>({
  renderOptionContent,
  renderPlaceholderContent,
  renderControlContent,
  icon,
  prefixIcon: PrefixIcon,
  withoutIndicatorRotation,
  notSeparated = false,
  isError = false,
  errorMessage,
  label,
  disabled,
  options,
  value,
  onChange,
  hideErrorMessageWrapper = false,
  placeholder,
  ...props
}: SelectProps<T, P>): ReactComponentReturnType {
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage({ id: 'forms.fields.select', defaultMessage: 'Select...' });
  const customStyles = useCustomStyles<P>(notSeparated, isError, false, !!PrefixIcon);
  const selectedItem = options?.find((option) => option.value === value);

  return (
    <FieldWrapper
      error={isError ? errorMessage : ''}
      hideErrorMessageWrapper={hideErrorMessageWrapper}
      isFakeLabel={!label}
      control={
        <div style={{ position: 'relative' }}>
          {PrefixIcon && (
            <span style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--muted)', display: 'flex',
              zIndex: 1, pointerEvents: 'none',
            }}>
              {React.createElement(PrefixIcon as React.ComponentType<{ width?: number; height?: number; style?: React.CSSProperties }>, { width: 14, height: 14, style: { fill: 'none' } })}
            </span>
          )}
        <ReactSelect<P>
          placeholder={resolvedPlaceholder}
          onChange={
            onChange
              ? (option) => {
                  if (option && option.value !== undefined) {
                    onChange(option.value);
                  }
                }
              : undefined
          }
          value={selectedItem || null}
          components={{
            SingleValue: ({ children, ...singleValueProps }) => (
              <components.SingleValue {...singleValueProps}>
                {renderControlContent
                  ? renderControlContent(
                      singleValueProps.selectProps.value as any
                    )
                  : children}
              </components.SingleValue>
            ),
            IndicatorsContainer: (indicatorsContainerProps) => (
              <IndicatorsContainer
                {...{
                  icon,
                  withoutIndicatorRotation,
                  ...indicatorsContainerProps,
                }}
              />
            ),
            Option: (optionProps) => (
              <Option {...{ renderOptionContent, ...optionProps }} />
            ),
            Placeholder: (placeholderProps) => (
              <Placeholder
                {...{ renderPlaceholderContent, ...placeholderProps }}
              />
            ),
          }}
          menuPlacement='auto'
          isRtl={theme.direction === 'rtl'}
          styles={customStyles}
          isDisabled={disabled}
          {...{ ...props, options }}
        />
        </div>
      }
      {...{ label }}
    />
  );
}

export default SelectBase;
